/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-unused-vars */
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Formik } from 'formik';

import { Alert as MuiAlert, Button, TextField as MuiTextField, Typography } from '@mui/material';
import { spacing } from '@mui/system';
import ROUTES from 'modules/common/constants/route';
import { forgotPasswordActions } from 'modules/forgot-password/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectNotification } from 'modules/common/notifications/selectors';
import { useEffect } from 'react';
import useCounter from 'modules/common/hooks/use-counter';
import TIME_OUTS from 'modules/common/constants/time-outs';
import forgetEmailFormValidation from 'modules/forgot-password/validations/forgot-password-form-validation';
import { notificationActions } from 'modules/common/notifications/slice';
import { ENVIRONMENT } from 'config';

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const { timeLeft, setTimeLeft } = useCounter();
  const notification = useSelector(selectNotification);
  //
  useEffect(() => {
    setTimeLeft(null);
    if (notification?.isEnabled) {
      setTimeLeft(TIME_OUTS.REDIRECT_TO_LOGIN);
    }
    return () => notification?.isEnabled && dispatch(notificationActions.resetNotification());
  }, [notification]);
  return (
    <Formik
      initialValues={{
        email: '',
      }}
      validationSchema={forgetEmailFormValidation}
      onSubmit={async (values) => {
        dispatch(forgotPasswordActions.forgotPassword(values));
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          {notification?.isEnabled && (
            <Alert mt={2} mb={1} severity={notification?.type}>
              {notification?.message}
            </Alert>
          )}
          {timeLeft && (
            <Typography component="h2" variant="body1" align="center">
              You will be redirected to the login page in {timeLeft} seconds
            </Typography>
          )}
          <TextField
            type="email"
            name="email"
            label="Email Address"
            value={values.email}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ backgroundColor: window.location.origin === ENVIRONMENT.APP_URL ? '#3F8CFC' :  "#009BCD"}}
            disabled={isSubmitting}
          >
            Request reset link
          </Button>
          <Button component={Link} to={ROUTES.LOGIN} fullWidth sx={{ color: window.location.origin === ENVIRONMENT.APP_URL ? '#3F8CFC' :  "#009BCD"}}>
            Return to Login
          </Button>
        </form>
      )}
    </Formik>
  );
};

export default ResetPasswordForm;
